import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Store, Search as SearchIcon, MapPin, ShoppingBag, PlusCircle, Settings } from "lucide-react";
import { toast } from "sonner";

const money = (cents, currency = "usd") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "usd").toUpperCase() }).format((cents || 0) / 100);

// Public shop page — /shop/{business_id}. Renders the business header + a
// searchable grid of active, in-stock products.

export default function BusinessShop() {
  const { business_id } = useParams();
  const { user } = useAuth();
  const [shop, setShop] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/shop/${business_id}/products`, { params: { q: q || undefined, sort } });
        if (!alive) return;
        setShop(r.data?.shop || null);
        setItems(r.data?.items || []);
      } catch (e) {
        if (alive) toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [business_id, q, sort]);

  // Owner check — the shop URL uses the owner's user id regardless of role
  // (business, influencer, band), so any signed-in user viewing their own
  // shop should see the "Manage" affordance.
  const isOwner = user && user.id === business_id;

  return (
    <div className="min-h-[100vh] pb-16" data-testid="business-shop-page">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-[#EAE3DE] bg-white">
        {shop?.hero_image_url ? (
          <div className="relative aspect-[16/5] md:aspect-[16/4]">
            <img src={shop.hero_image_url} alt={shop.business_name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute inset-x-0 bottom-0 px-6 md:px-10 py-6 text-white">
              <div className="max-w-6xl mx-auto">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur border border-white/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider">
                  <Store size={12} /> Shop
                </div>
                <h1 className="mt-3 text-3xl md:text-5xl font-black tracking-tight">{shop?.business_name || "Shop"}</h1>
                {(shop?.business_city || shop?.business_state) && (
                  <p className="mt-1 text-sm text-white/80 inline-flex items-center gap-1">
                    <MapPin size={12} /> {[shop?.business_city, shop?.business_state].filter(Boolean).join(", ")}
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-12">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-[#F9E4E1] border border-[#F0C9C4] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#B31E1E]">
              <Store size={12} /> Shop
            </div>
            <h1 className="mt-3 text-4xl md:text-5xl font-black tracking-tight text-[#1B1614]">{shop?.business_name || "Shop"}</h1>
            {shop?.business_bio && (
              <p className="mt-3 text-[15px] text-[#5C4E48] max-w-2xl">{shop.business_bio}</p>
            )}
            {(shop?.business_city || shop?.business_state) && (
              <p className="mt-2 text-sm text-[#5C4E48] inline-flex items-center gap-1">
                <MapPin size={12} /> {[shop?.business_city, shop?.business_state].filter(Boolean).join(", ")}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Owner action strip */}
      {isOwner && (
        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-4">
          <Card className="rounded-md p-3 flex items-center justify-between border border-amber-300 bg-amber-50" data-testid="shop-owner-strip">
            <p className="text-xs text-amber-900">
              This is your shop. Add products, go live, or manage orders.
            </p>
            <div className="flex gap-2">
              <Link to="/shop/manage">
                <Button size="sm" variant="outline" className="rounded-sm border-amber-400 text-amber-900 hover:bg-amber-100" data-testid="shop-manage-link">
                  <Settings size={13} className="mr-1.5" /> Manage
                </Button>
              </Link>
              <Link to="/shop/manage?new=1">
                <Button size="sm" className="rounded-sm bg-[#B31E1E] hover:bg-[#8F1717] text-white" data-testid="shop-new-product-link">
                  <PlusCircle size={13} className="mr-1.5" /> Add product
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      )}

      {/* Search + Sort */}
      <div className="max-w-6xl mx-auto px-6 md:px-10 pt-6">
        <div className="flex flex-wrap gap-3 items-center mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search products" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-sm pl-9" data-testid="shop-search-input" />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="shop-sort-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Newest</SelectItem>
              <SelectItem value="price_asc">Price: low → high</SelectItem>
              <SelectItem value="price_desc">Price: high → low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="shop-loading">
            {Array.from({ length: 8 }).map((_, i) => (
              <Card key={i} className="rounded-md overflow-hidden">
                <div className="aspect-square bg-gray-100 animate-pulse" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-gray-100 rounded animate-pulse w-2/3" />
                  <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2" />
                </div>
              </Card>
            ))}
          </div>
        ) : items.length === 0 ? (
          <Card className="rounded-md p-10 text-center" data-testid="shop-empty">
            <ShoppingBag size={30} className="mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">
              {isOwner
                ? <>No products yet. <Link to="/shop/manage?new=1" className="text-[#B31E1E] hover:underline">Add your first product</Link>.</>
                : "This shop hasn't posted any products yet."}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="shop-grid">
            {items.map((p) => (
              <Link key={p.id} to={`/shop/${business_id}/product/${p.id}`} className="group" data-testid={`product-card-${p.id}`}>
                <Card className="rounded-md overflow-hidden hover:border-[#B31E1E]/40 transition-colors h-full">
                  <div className="relative aspect-square bg-gray-100 overflow-hidden">
                    {p.photos?.[0] ? (
                      <img src={p.photos[0]} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <ShoppingBag size={36} />
                      </div>
                    )}
                    {!p.in_stock && (
                      <Badge className="absolute top-2 right-2 rounded-sm bg-gray-900 text-white hover:bg-gray-900 text-[10px]">Sold out</Badge>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="font-medium text-[#1B1614] truncate">{p.title}</div>
                    <div className="mt-1 font-black text-[#B31E1E]">{money(p.price_cents, p.currency)}</div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
